/**
 * Itens de um documento de estoque que só leva produto + unidade + quantidade (transferência,
 * carregamento da carga). O lote é sempre escolhido pelo sistema (FEFO, nunca vencido).
 */
import { useEffect } from "react";
import { Button, Field, Input, Select } from "@/components/ui";
import { toDecimal } from "@/lib/decimal";
import { qty, unitsOf, useProductDetail } from "./lib";
import { ProductPicker } from "./pickers";

export type DocItemLine = {
  key: number;
  productId: string;
  productLabel: string;
  unitId: string;
  quantity: string;
};

export const emptyDocItem = (key: number): DocItemLine => ({
  key,
  productId: "",
  productLabel: "",
  unitId: "",
  quantity: "",
});

export const docItemsBody = (lines: DocItemLine[]) =>
  lines.map((l) => ({
    productId: l.productId,
    unitId: l.unitId,
    quantity: toDecimal(l.quantity),
  }));

export function DocItemsEditor({
  lines,
  onChange,
}: {
  lines: DocItemLine[];
  onChange: (lines: DocItemLine[]) => void;
}) {
  return (
    <div className="space-y-3">
      {lines.map((l, i) => (
        <DocItemRow
          key={l.key}
          line={l}
          onChange={(v) => onChange(lines.map((x) => (x.key === v.key ? v : x)))}
          onRemove={lines.length > 1 ? () => onChange(lines.filter((_, j) => j !== i)) : undefined}
        />
      ))}
      <Button
        size="sm"
        variant="secondary"
        onClick={() =>
          onChange([...lines, emptyDocItem(Math.max(0, ...lines.map((x) => x.key)) + 1)])
        }
      >
        Adicionar item
      </Button>
    </div>
  );
}

function DocItemRow({
  line,
  onChange,
  onRemove,
}: {
  line: DocItemLine;
  onChange: (l: DocItemLine) => void;
  onRemove: (() => void) | undefined;
}) {
  const product = useProductDetail(line.productId);
  const units = unitsOf(product.data);
  const firstUnit = units[0]?.id;
  // escolhido o produto, assume a unidade-base até o usuário trocar
  useEffect(() => {
    if (!line.unitId && firstUnit) onChange({ ...line, unitId: firstUnit });
  }, [line, firstUnit, onChange]);
  return (
    <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-12 sm:items-end">
      <div className="sm:col-span-6">
        <Field label="Produto">
          <ProductPicker
            value={line.productId}
            label={line.productLabel}
            onChange={(id, label) =>
              onChange({ ...line, productId: id, productLabel: label, unitId: "" })
            }
          />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Unidade">
          <Select
            value={line.unitId || units[0]?.id || ""}
            onChange={(e) => onChange({ ...line, unitId: e.target.value })}
            disabled={!product.data}
          >
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
                {u.factor !== "1" ? ` (${qty(u.factor)} ${product.data?.baseUnitCode})` : ""}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Quantidade">
          <Input
            inputMode="decimal"
            value={line.quantity}
            onChange={(e) =>
              onChange({
                ...line,
                quantity: e.target.value,
                unitId: line.unitId || units[0]?.id || "",
              })
            }
          />
        </Field>
      </div>
      <div className="text-right sm:col-span-2">
        {onRemove && (
          <Button size="sm" variant="ghost" onClick={onRemove}>
            Remover
          </Button>
        )}
      </div>
    </div>
  );
}
